import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const HeadToHeadPerformanceChart = ({ h2hData = [], selectedManager = 'Kevin', className = "" }) => {
  // Prepare chart data
  const prepareChartData = () => {
    if (!h2hData.length || !selectedManager) return { dotData: [], barData: [], opponentStats: [] };

    // Filter data for selected manager
    const managerData = h2hData.filter(row => row.Manager1 === selectedManager);
    
    if (!managerData.length) return { dotData: [], barData: [], opponentStats: [] };

    // Group by opponent and count wins/losses
    const opponentCounts = {};
    managerData.forEach(game => {
      const opponent = game.Manager2;
      if (!opponentCounts[opponent]) {
        opponentCounts[opponent] = { wins: 0, losses: 0, total: 0, games: [] };
      }
      opponentCounts[opponent].total++;
      opponentCounts[opponent].games.push(game);
      if (game.Outcome === 'Win') opponentCounts[opponent].wins++;
      else if (game.Outcome === 'Loss') opponentCounts[opponent].losses++;
    });

    // Sort opponents by total games (most games first) and get unique list
    const sortedOpponents = Object.entries(opponentCounts)
      .sort(([,a], [,b]) => b.total - a.total)
      .map(([opponent]) => opponent);

    // Create bar data for background bars - one entry per opponent
    const barData = sortedOpponents.map((opponent) => ({
      opponent,
      losses: opponentCounts[opponent].losses,
      wins: opponentCounts[opponent].wins,
      total: opponentCounts[opponent].total
    }));

    // Create dots data - positioned within their respective bar sections
    const dotData = [];
    const opponentStats = [];
    
    sortedOpponents.forEach((opponent, opponentIndex) => {
      const opponentGames = opponentCounts[opponent].games;
      
      // Separate wins and losses
      const wins = opponentGames.filter(game => game.Outcome === 'Win');
      const losses = opponentGames.filter(game => game.Outcome === 'Loss');
      
      const dotsPerRow = 2; // Two dots per row
      const dotSpacing = 0.1; // Small horizontal spacing between dots
      const rowHeight = 1; // Vertical spacing between rows
      
      let currentRow = 0;
      
      // Position loss dots first (bottom section)
      losses.forEach((game, gameIndex) => {
        const row = Math.floor(gameIndex / dotsPerRow);
        const col = gameIndex % dotsPerRow;
        const xOffset = (col - 0.5) * dotSpacing; // Center two dots
        
        dotData.push({
          x: opponentIndex + xOffset,
          y: row * rowHeight,
          opponent: opponent,
          outcome: game.Outcome,
          gameID: game.GameID,
          yearWeek: game.YearWeek,
          pointsFor: game.PF,
          pointsAgainst: game.PA,
          opponentIndex: opponentIndex
        });
        
        currentRow = row + 1;
      });
      
      // Position win dots directly after losses (no gap)
      wins.forEach((game, gameIndex) => {
        const row = Math.floor(gameIndex / dotsPerRow);
        const col = gameIndex % dotsPerRow;
        const xOffset = (col - 0.5) * dotSpacing; // Center two dots
        
        dotData.push({
          x: opponentIndex + xOffset,
          y: (currentRow + row) * rowHeight,
          opponent: opponent,
          outcome: game.Outcome,
          gameID: game.GameID,
          yearWeek: game.YearWeek,
          pointsFor: game.PF,
          pointsAgainst: game.PA,
          opponentIndex: opponentIndex
        });
      });

      // Store opponent stats for bar chart and tooltip
      opponentStats.push({
        opponent,
        index: opponentIndex,
        wins: wins.length,
        losses: losses.length,
        total: opponentGames.length,
        winPct: ((wins.length / opponentGames.length) * 100).toFixed(1)
      });
    });

    return { dotData, barData, opponentStats };
  };

  const { dotData, opponentStats } = prepareChartData();

  if (!dotData.length) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
          <span className="hidden sm:inline">
            {selectedManager}'s performance history against all other managers.
          </span>
          <span className="sm:hidden">
            {selectedManager} vs All Managers
          </span>
        </h3>
        <p className="text-gray-500">No data available for {selectedManager}</p>
      </div>
    );
  }

  // Custom tooltip component with mobile optimization
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dot = payload[0].payload;
      const opponentStat = opponentStats.find(stat => stat.opponent === dot.opponent);
      
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-3 sm:p-4 max-w-xs sm:max-w-sm">
          <div className="space-y-1 sm:space-y-1.5">
            <p className="font-semibold text-gray-900 text-sm sm:text-base">vs {dot.opponent}</p>
            
            {/* Mobile-first layout */}
            <div className="grid grid-cols-1 gap-1 text-xs sm:text-sm">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Game</span>
                <span className="text-gray-900">{dot.yearWeek}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Result</span>
                <span className={`font-semibold ${dot.outcome === 'Win' ? 'text-purple-600' : 'text-orange-600'}`}>
                  {dot.outcome}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Score</span>
                <span className="text-gray-900 font-medium">{dot.pointsFor?.toFixed(1)} - {dot.pointsAgainst?.toFixed(1)}</span>
              </div>
            </div>
            
            <div className="border-t pt-1 sm:pt-2 mt-1 sm:mt-2">
              <p className="text-xs sm:text-sm font-medium text-gray-700">Overall Record:</p>
              <p className="text-xs sm:text-sm text-gray-900">
                {opponentStat?.wins}W - {opponentStat?.losses}L ({opponentStat?.winPct}%)
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-white rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 ${className}`}>
      {/* Title with mobile optimization */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 leading-tight">
          <span className="hidden sm:inline">
            {selectedManager}'s performance history against all other managers.
          </span>
          <span className="sm:hidden">
            {selectedManager} vs All Managers
          </span>
        </h3>
        <p className="text-gray-600 text-sm lg:text-base">
          <span className="hidden sm:inline">
            Each dot represents one game, stacked to form bars. Orange dots (losses) on bottom, purple dots (wins) on top.
          </span>
          <span className="sm:hidden">
            Dots = games • Orange = losses, Purple = wins
          </span>
        </p>
      </div>

      {/* Chart with responsive height */}
      <div className="h-64 sm:h-80 lg:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            data={dotData}
            margin={{
              top: 10,
              right: 10,
              left: 15,
              bottom: window.innerWidth < 640 ? 40 : 60,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis 
              type="number"
              dataKey="x"
              domain={[-0.5, opponentStats.length - 0.5]}
              stroke="#6b7280"
              fontSize={window.innerWidth < 640 ? 9 : 12}
              tickLine={false}
              axisLine={false}
              angle={window.innerWidth < 640 ? -45 : 0}
              textAnchor={window.innerWidth < 640 ? 'end' : 'middle'}
              tickFormatter={(value) => {
                const roundedValue = Math.round(value);
                const opponent = opponentStats[roundedValue]?.opponent || '';
                // Truncate long names on mobile
                return window.innerWidth < 640 && opponent.length > 6 
                  ? opponent.substring(0, 5) + '...' 
                  : opponent;
              }}
              ticks={opponentStats.map((_, index) => index)}
            />
            <YAxis 
              type="number"
              dataKey="y"
              stroke="#6b7280"
              fontSize={10}
              tickLine={false}
              axisLine={false}
              tick={false}
              width={window.innerWidth < 640 ? 20 : 30}
              label={window.innerWidth >= 640 ? { 
                value: 'Games Played', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#6b7280', fontSize: '11px' }
              } : undefined}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Scatter plot with responsive dot sizing */}
            <Scatter 
              dataKey="y"
              shape={(props) => {
                const { payload, cx, cy } = props;
                const color = payload.outcome === 'Win' ? '#8b5cf6' : '#fb923c';
                const radius = window.innerWidth < 640 ? 6 : 8;
                const strokeWidth = window.innerWidth < 640 ? 1.5 : 2;
                
                return (
                  <circle 
                    cx={cx} 
                    cy={cy} 
                    r={radius} 
                    fill={color}
                    stroke="#ffffff"
                    strokeWidth={strokeWidth}
                    opacity={0.9}
                  />
                );
              }}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Mobile-optimized legend */}
      <div className="mt-3 sm:mt-4">
        {/* Mobile: Compact horizontal layout */}
        <div className="flex items-center justify-center space-x-4 sm:space-x-6 text-xs sm:text-sm">
          <div className="flex items-center space-x-1 sm:space-x-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-600 rounded-full border border-white"></div>
            <span className="text-gray-700">Wins</span>
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2">
            <div className="w-3 h-3 sm:w-4 sm:h-4 bg-orange-400 rounded-full border border-white"></div>
            <span className="text-gray-700">Losses</span>
          </div>
          <div className="hidden sm:block text-gray-500 text-xs">
            Hover for game details
          </div>
          <div className="sm:hidden text-gray-500 text-xs">
            Tap for details
          </div>
        </div>
      </div>

      {/* Mobile-specific summary stats */}
      <div className="mt-4 sm:hidden">
        <div className="grid grid-cols-2 gap-2 text-xs">
          {opponentStats.slice(0, 4).map((stat, index) => (
            <div key={stat.opponent} className="bg-gray-50 rounded-lg p-2">
              <div className="font-medium text-gray-900 truncate">{stat.opponent}</div>
              <div className="text-gray-600">{stat.wins}W-{stat.losses}L ({stat.winPct}%)</div>
            </div>
          ))}
        </div>
        {opponentStats.length > 4 && (
          <div className="text-center mt-2">
            <span className="text-xs text-gray-500">
              +{opponentStats.length - 4} more opponents
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeadToHeadPerformanceChart;